import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useCart } from '@/context/CartContext.jsx';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import VariantSelectionModal from '@/components/VariantSelectionModal';
import CustomItemModal from '@/components/CustomItemModal';

function ProductCard({ p, onAdd, showImages = true, columns = 4 }) {
  // Dynamic sizing based on column count
  const isCompact = columns >= 6;
  const isVeryCompact = columns >= 8;
  
  const padding = isVeryCompact ? 'p-2' : isCompact ? 'p-3' : 'p-4';
  const imageMargin = isVeryCompact ? 'mb-1' : isCompact ? 'mb-2' : 'mb-3';
  const titleSize = isVeryCompact ? 'text-xs' : isCompact ? 'text-sm' : 'text-lg';
  const titleMargin = isVeryCompact ? 'mb-1' : isCompact ? 'mb-1' : 'mb-2';
  const priceSize = isVeryCompact ? 'text-sm' : isCompact ? 'text-lg' : 'text-2xl';
  const priceMargin = isVeryCompact ? 'mb-1' : isCompact ? 'mb-2' : 'mb-3';
  const buttonHeight = isVeryCompact ? 'h-8' : isCompact ? 'h-10' : 'h-14';
  const buttonText = isVeryCompact ? 'text-xs' : isCompact ? 'text-sm' : 'text-lg';
  const iconSize = isVeryCompact ? 'w-6 h-6' : isCompact ? 'w-8 h-8' : 'w-12 h-12';
  
  return (
    <Card className="hover:shadow-md transition-shadow h-full flex flex-col" data-testid={`product-${p.sku}`}>
      <CardContent className={`${padding} flex-1 flex flex-col`}>
        {showImages && (
          <div className={`aspect-square ${imageMargin} rounded-md overflow-hidden bg-gray-100 flex items-center justify-center`}>
            {p.image ? (
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            ) : (
              <svg className={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-2.25z" />
              </svg>
            )}
          </div>
        )}
        <div className="flex-1 flex flex-col">
          <h3 className={`font-medium ${titleSize} ${titleMargin} line-clamp-2`}>{p.name}</h3>
          <span className={`${priceSize} font-bold ${priceMargin}`}>${p.price.toFixed(2)}</span>
          <Button 
            data-testid={`add-${p.sku}`} 
            className={`w-full ${buttonHeight} ${buttonText} mt-auto`}
            onClick={() => onAdd(p)}
          >
            {p.hasMultipleVariants ? 'Select' : (isVeryCompact ? 'Add' : 'Add to Cart')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProductListItem({ p, onAdd, showImages = true }) {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`product-${p.sku}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          {showImages && (
            <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
              {p.image ? (
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-2.25z" />
                </svg>
              )}
            </div>
          )}
          <h3 className="flex-1 font-medium text-base truncate min-w-0">{p.name}</h3>
          <span className="text-lg font-bold flex-shrink-0 min-w-[80px] text-right">${p.price.toFixed(2)}</span>
          <Button 
            data-testid={`add-${p.sku}`} 
            className="h-10 w-24 flex-shrink-0" 
            onClick={() => onAdd(p)}
          >
            {p.hasMultipleVariants ? 'Select' : 'Add'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Special card for adding custom items
function AddCustomItemCard({ onClick, showImages = true, columns = 4 }) {
  const isCompact = columns >= 6;
  const isVeryCompact = columns >= 8;
  
  const padding = isVeryCompact ? 'p-2' : isCompact ? 'p-3' : 'p-4';
  const imageMargin = isVeryCompact ? 'mb-1' : isCompact ? 'mb-2' : 'mb-3';
  const titleSize = isVeryCompact ? 'text-xs' : isCompact ? 'text-sm' : 'text-lg';
  const titleMargin = isVeryCompact ? 'mb-1' : isCompact ? 'mb-1' : 'mb-2';
  const priceSize = isVeryCompact ? 'text-sm' : isCompact ? 'text-lg' : 'text-2xl';
  const priceMargin = isVeryCompact ? 'mb-1' : isCompact ? 'mb-2' : 'mb-3';
  const buttonHeight = isVeryCompact ? 'h-8' : isCompact ? 'h-10' : 'h-14';
  const buttonText = isVeryCompact ? 'text-xs' : isCompact ? 'text-sm' : 'text-lg';
  const iconSize = isVeryCompact ? 'w-8 h-8' : isCompact ? 'w-10 h-10' : 'w-16 h-16';
  
  return (
    <Card 
      className="hover:shadow-md transition-shadow h-full flex flex-col border-2 border-dashed border-primary/50 bg-primary/5 cursor-pointer hover:border-primary hover:bg-primary/10" 
      onClick={onClick}
      data-testid="add-custom-item-card"
    >
      <CardContent className={`${padding} flex-1 flex flex-col`}>
        {showImages && (
          <div className={`aspect-square ${imageMargin} rounded-md overflow-hidden bg-primary/10 flex items-center justify-center`}>
            {/* Custom icon - pencil with price tag */}
            <svg className={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </div>
        )}
        <div className="flex-1 flex flex-col">
          <h3 className={`font-medium ${titleSize} ${titleMargin} line-clamp-2 text-primary`}>Add Custom Item</h3>
          <span className={`${priceSize} font-bold ${priceMargin} text-primary`}>Custom</span>
          <Button 
            className={`w-full ${buttonHeight} ${buttonText} mt-auto`}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            {isVeryCompact ? 'Create' : 'Create Item'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// List view version of Add Custom Item
function AddCustomItemListItem({ onClick, showImages = true }) {
  return (
    <Card 
      className="hover:shadow-md transition-shadow border-2 border-dashed border-primary/50 bg-primary/5 cursor-pointer hover:border-primary hover:bg-primary/10" 
      onClick={onClick}
      data-testid="add-custom-item-list"
    >
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          {showImages && (
            <div className="w-12 h-12 rounded-md overflow-hidden bg-primary/10 flex-shrink-0 flex items-center justify-center">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
          )}
          <h3 className="flex-1 font-medium text-base text-primary">Add Custom Item</h3>
          <span className="text-lg font-bold flex-shrink-0 min-w-[80px] text-right text-primary">Custom</span>
          <Button 
            className="h-10 w-24 flex-shrink-0" 
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            Create
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Sale() {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(true);
  const { toast } = useToast();
  
  // Load display settings
  const [displaySettings, setDisplaySettings] = useState(() => {
    const s = localStorage.getItem('display_settings');
    return s ? JSON.parse(s) : {
      columns: 4,
      showImages: true,
      viewMode: 'grid'
    };
  });

  // Load display settings from backend for POS users
  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const { data } = await api.get('/auth/me');
        
        // If POS user, load their display settings from the backend
        if (data.user_type === 'pos_user' && data.display_settings) {
          console.log('[Sale] Loading POS user display settings:', data.display_settings);
          setDisplaySettings({
            columns: data.display_settings.columns || 4,
            showImages: data.display_settings.show_images !== undefined ? data.display_settings.show_images : true,
            viewMode: data.display_settings.view_mode || 'grid'
          });
        } else {
          // Admin user - settings already loaded from localStorage in initial state
          console.log('[Sale] Using admin localStorage settings');
        }
      } catch (error) {
        console.error('[Sale] Failed to load user settings:', error);
      }
    };
    loadUserSettings();
  }, []);
  const { items, addItem, updateQty, removeItem, subtotal, discountAmount, setDiscountAmount, discountPercent, setDiscountPercent, tax, total, clear, taxRate } = useCart();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customItemModalOpen, setCustomItemModalOpen] = useState(false);
  const clearRan = useRef(false);
  
  // Clear cart when returning from payment - guard with ref to prevent loops
  useEffect(() => {
    if (!clearRan.current && location.state?.clearCart) {
      clearRan.current = true;
      clear();
      setSelectedCustomer(null); // Also clear customer
      // Clear the state so it doesn't clear again on refresh
      navigate('/sale', { replace: true, state: {} });
    }
    
    // Restore customer if coming back from checkout
    if (location.state?.selectedCustomer) {
      setSelectedCustomer(location.state.selectedCustomer);
    }
    
    // Load draft invoice items into cart
    if (location.state?.loadItems && location.state?.draftInvoice) {
      const draftItems = location.state.loadItems;
      console.log('[Sale] Loading draft items into cart:', draftItems);
      
      // Clear cart first
      clear();
      
      // Add each item to cart
      draftItems.forEach(item => {
        const cartItem = {
          id: item.product_id || item.productId || `draft-${item.name}`,
          name: item.name,
          price: item.amount || item.price || 0,
          image: item.image || null,
          variantName: item.variantName || null
        };
        
        const quantity = item.qty || item.quantity || 1;
        for (let i = 0; i < quantity; i++) {
          addItem(cartItem);
        }
      });
      
      // Clear the state so it doesn't load again
      navigate('/sale', { replace: true, state: { selectedCustomer: location.state.selectedCustomer } });
    }
  }, [location.state, clear, navigate, addItem]);
  
  const [contactId, setContactId] = useState('');
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [manualDiscountType, setManualDiscountType] = useState('percent');
  const [manualDiscountValue, setManualDiscountValue] = useState('');
  const [couponScanCode, setCouponScanCode] = useState('');

  useEffect(() => {
    // Create new sale/contact
    api.post('/sale/new').then((r) => setContactId(r.data.contact_id));
    
    // Always load live products from GoHighLevel
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        // Fetch live products from GHL
        const { data } = await api.get(`/live/products`, { params: { live: 1, limit: 100 } });
        const raw = data.products || data.items || data || [];
        const mapped = (raw || []).map((p) => {
          // Use SKU if it's a non-empty string, otherwise fall back to ID
          const sku = (typeof p.sku === 'string' && p.sku.trim()) ? p.sku.trim() : (p._id || p.id);
          
          console.log(`[PRODUCT MAPPING] ${p.name}:`, {
            sku,
            hasMultipleVariants: p.hasMultipleVariants,
            variants: p.variants?.length || 0
          });
          
          return {
            _id: p._id,
            sku: sku,
            name: p.name || 'Unnamed Product',
            price: parseFloat(p.price || 0),
            taxable: true, // ALL regular products are taxable - ignore GHL isTaxesEnabled
            image: p.image || null,
            variants: p.variants || [],
            hasMultipleVariants: p.hasMultipleVariants || false,
          };
        });
        console.log(`[PRODUCTS LOADED] Total: ${mapped.length} products`);
        console.log('[PRODUCTS LOADED] SKUs:', mapped.map(p => p.sku));
        setProducts(mapped);
        if (mapped.length === 0) {
          toast({ title: 'No Products', description: 'No products found in your CRM. Please add products first.' });
        }
      } catch (err) {
        console.error('Failed to load products:', err);
        toast({ 
          title: 'Error Loading Products', 
          description: 'Could not load products from your CRM. Please check your connection.',
          variant: 'destructive'
        });
      } finally {
        setLoadingProducts(false);
      }
    };
    
    loadProducts();
  }, []); // No dependencies - only run once on mount

  const onScan = useCallback((code) => {
    const normalizedCode = code.toLowerCase().trim();
    console.log(`[BARCODE SCAN] Scanned code: "${code}", normalized: "${normalizedCode}"`);
    
    // Search through all products and their variants
    for (const product of products) {
      // Check if product has variants
      if (product.variants && product.variants.length > 0) {
        // Search variants for matching SKU
        const matchingVariant = product.variants.find(v => 
          v.sku && v.sku.toLowerCase().trim() === normalizedCode
        );
        
        if (matchingVariant) {
          // Found a variant match - add it directly to cart
          const variantProduct = {
            ...product,
            sku: matchingVariant.sku,
            price: matchingVariant.price,
            variantName: matchingVariant.name,
            image: matchingVariant.image || product.image,
            price_id: matchingVariant.price_id
          };
          console.log(`[BARCODE SCAN] Found variant:`, variantProduct);
          addItem(variantProduct);
          return;
        }
      } else {
        // No variants - check base product SKU
        if (product.sku && product.sku.toLowerCase().trim() === normalizedCode) {
          console.log(`[BARCODE SCAN] Found product:`, product);
          addItem(product);
          return;
        }
      }
    }
    
    console.log(`[BARCODE SCAN] No product found for normalized code: "${normalizedCode}"`);
    toast({ title: 'Not Found', description: `No product found for barcode: ${code}` });
  }, [products, addItem, toast]);
  useBarcodeScanner(onScan);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return products.filter((p) => 
      p.name.toLowerCase().includes(q) || 
      (p.sku && p.sku.toLowerCase().includes(q))
    );
  }, [products, query]);

  // Load coupons when dialog opens
  const loadCoupons = async () => {
    try {
      const { data } = await api.get('/live/coupons', { params: { live: 1 } });
      setCoupons(data.coupons || []);
    } catch (err) {
      console.error('Failed to load coupons:', err);
      toast({ title: 'Error', description: 'Could not load coupons' });
    }
  };

  // Apply selected coupon
  const applyCoupon = (coupon) => {
    if (coupon.type === 'percent') {
      setDiscountPercent(coupon.discount / 100);  // GHL sends as whole number (20 = 20%)
      setDiscountAmount(0);
    } else {
      setDiscountAmount(coupon.discount);
      setDiscountPercent(0);
    }
    toast({ title: 'Coupon Applied', description: `${coupon.name} - ${coupon.code}` });
    setCouponDialogOpen(false);
  };

  // Apply manual discount
  const applyManualDiscount = () => {
    const value = parseFloat(manualDiscountValue);
    if (isNaN(value) || value <= 0) {
      toast({ title: 'Invalid', description: 'Enter a valid discount amount' });
      return;
    }
    
    if (manualDiscountType === 'percent') {
      setDiscountPercent(value / 100);  // Convert 10 to 0.10
      setDiscountAmount(0);
    } else {
      setDiscountAmount(value);
      setDiscountPercent(0);
    }
    toast({ title: 'Discount Applied', description: `${manualDiscountType === 'percent' ? value + '%' : '$' + value} discount` });
    setCouponDialogOpen(false);
    setManualDiscountValue('');
  };

  // Handle add to cart - check if product has multiple variants
  const handleAddToCart = (product) => {
    console.log(`[ADD TO CART] Product:`, product);
    
    // If product has multiple variants, show modal to select
    if (product.hasMultipleVariants && product.variants && product.variants.length > 1) {
      console.log(`[ADD TO CART] Opening variant modal for product with ${product.variants.length} variants`);
      setSelectedProduct(product);
      setVariantModalOpen(true);
    } else {
      // Single variant or no variants - add directly to cart
      console.log(`[ADD TO CART] Adding product directly (single/no variants)`);
      addItem(product);
    }
  };

  // Handle variant selection from modal
  const handleSelectVariant = (variantProduct) => {
    console.log(`[VARIANT SELECTED]`, variantProduct);
    addItem(variantProduct);
  };

  // Handle barcode scan for coupon
  const scanCoupon = async () => {
    if (!couponScanCode) return;
    const found = coupons.find(c => c.code === couponScanCode);
    if (found) {
      applyCoupon(found);
    } else {
      toast({ title: 'Invalid', description: 'Coupon code not found' });
    }
    setCouponScanCode('');
  };

  // Handle custom item addition
  const handleAddCustomItem = (customItem) => {
    console.log('[CUSTOM ITEM] Adding:', customItem);
    
    // Add item multiple times if quantity > 1
    for (let i = 0; i < customItem.qty; i++) {
      addItem({
        ...customItem,
        qty: 1 // Add one at a time
      });
    }
    
    toast({ 
      title: 'Custom Item Added', 
      description: `${customItem.name} x${customItem.qty} added to cart${customItem.taxable ? ' (taxable)' : ' (no tax)'}` 
    });
  };

  const checkout = async () => {
    if (!items.length) {
      toast({ title: 'Empty Cart', description: 'Add items before checkout' });
      return;
    }

    try {
      // Calculate discount info
      let discountType = 'percentage';
      let discountValue = 0;
      
      if (discountPercent > 0) {
        discountType = 'percentage';
        discountValue = discountPercent * 100; // Convert 0.10 to 10
      } else if (discountAmount > 0) {
        discountType = 'fixed';
        discountValue = discountAmount;
      }
      
      // Prepare cart data to pass to payment page
      // Don't create invoice yet - wait until customer is added and payment is processed
      const cartData = {
        contactId: contactId || undefined,
        items: items.map(({ sku, name, qty, price, variantName, image, price_id }) => ({ 
          productId: sku,
          name: name,
          qty: qty,
          price: price,
          variantName: variantName,
          image: image,
          price_id: price_id
        })),
        discount: {
          type: discountType,
          value: discountValue,
          amount: discountAmount // Add the calculated discount amount
        },
        discountTotal: discountAmount, // Total discount applied
        subtotal,
        tax,
        total,
        taxRate,
        selectedCustomer: selectedCustomer // Include previously selected customer
      };
      
      // Navigate to invoice payment page with cart data (invoice will be created later)
      navigate('/invoice-payment/new', {
        state: { cartData }
      });
      
    } catch (e) {
      console.error('Checkout failed:', e);
      toast({ 
        title: 'Checkout Failed', 
        description: e.response?.data?.detail || 'Could not create invoice',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="p-2 md:p-4 px-8 md:px-12 w-full">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-8">
          <div className="flex items-center gap-2 mb-3">
            <Input data-testid="search-input" placeholder="Search or scan..." value={query} onChange={(e) => setQuery(e.target.value)} className="flex-1 h-12 text-lg" />
            <Dialog open={couponDialogOpen} onOpenChange={(open) => { setCouponDialogOpen(open); if (open) loadCoupons(); }}>
              <DialogTrigger asChild>
                <Button data-testid="apply-coupon-button" variant="secondary" className="h-12 text-lg px-6">
                  Apply Discount
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Apply Discount or Coupon</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Manual Discount */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Manual Discount</h3>
                    <div className="flex items-center gap-2">
                      <select 
                        value={manualDiscountType} 
                        onChange={(e) => setManualDiscountType(e.target.value)}
                        className="h-12 px-3 border rounded-md"
                      >
                        <option value="percent">Percentage (%)</option>
                        <option value="fixed">Fixed Amount ($)</option>
                      </select>
                      <Input 
                        type="number" 
                        placeholder={manualDiscountType === 'percent' ? 'e.g. 10 for 10%' : 'e.g. 5.00'}
                        value={manualDiscountValue}
                        onChange={(e) => setManualDiscountValue(e.target.value)}
                        className="flex-1 h-12"
                      />
                      <Button onClick={applyManualDiscount} className="h-12 px-6">Apply</Button>
                    </div>
                  </div>

                  {/* Barcode Scan */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Scan Coupon Barcode</h3>
                    <div className="flex items-center gap-2">
                      <Input 
                        placeholder="Scan or enter coupon code..."
                        value={couponScanCode}
                        onChange={(e) => setCouponScanCode(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') scanCoupon(); }}
                        className="flex-1 h-12"
                      />
                      <Button onClick={scanCoupon} className="h-12 px-6">Scan</Button>
                    </div>
                  </div>

                  {/* Available Coupons */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Available Coupons</h3>
                    {coupons.length === 0 ? (
                      <p className="text-sm opacity-70">No coupons available</p>
                    ) : (
                      <div className="grid gap-2">
                        {coupons
                          .filter((c) => {
                            // Filter by scan code input
                            if (!couponScanCode) return true;
                            const search = couponScanCode.toLowerCase();
                            return (
                              c.name.toLowerCase().includes(search) ||
                              c.code.toLowerCase().includes(search)
                            );
                          })
                          .map((c) => (
                            <Card key={c.id || c.code} className="cursor-pointer hover:bg-accent" onClick={() => applyCoupon(c)}>
                              <CardContent className="p-4 flex items-center justify-between">
                                <div>
                                  <div className="font-semibold">{c.name}</div>
                                  <div className="text-sm opacity-70">{c.code}</div>
                                </div>
                                <div className="text-lg font-bold">
                                  {c.type === 'percent' ? `${c.discount}%` : `$${c.discount}`}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        {coupons.filter((c) => {
                          if (!couponScanCode) return false;
                          const search = couponScanCode.toLowerCase();
                          return (
                            c.name.toLowerCase().includes(search) ||
                            c.code.toLowerCase().includes(search)
                          );
                        }).length === 0 && couponScanCode && (
                          <p className="text-sm opacity-70 text-center py-4">No coupons match "{couponScanCode}"</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Loading Screen */}
          {loadingProducts ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
              <p className="text-lg font-medium text-gray-600">Loading Products...</p>
              <p className="text-sm text-gray-500 mt-2">Please wait while we fetch your products</p>
            </div>
          ) : displaySettings.viewMode === 'list' ? (
            <div className="space-y-2">
              {/* Always show Add Custom Item first in list view */}
              <AddCustomItemListItem 
                onClick={() => setCustomItemModalOpen(true)} 
                showImages={displaySettings.showImages} 
              />
              {filtered.map((p) => (
                <ProductListItem key={p.sku} p={p} onAdd={handleAddToCart} showImages={displaySettings.showImages} />
              ))}
            </div>
          ) : (
            <div 
              className="grid gap-3"
              style={{ 
                gridTemplateColumns: `repeat(${displaySettings.columns}, minmax(0, 1fr))` 
              }}
            >
              {/* Always show Add Custom Item first in grid view */}
              <AddCustomItemCard 
                onClick={() => setCustomItemModalOpen(true)} 
                showImages={displaySettings.showImages}
                columns={displaySettings.columns}
              />
              {filtered.map((p) => (
                <ProductCard 
                  key={p.sku} 
                  p={p} 
                  onAdd={handleAddToCart} 
                  showImages={displaySettings.showImages}
                  columns={displaySettings.columns}
                />
              ))}
            </div>
          )}
        </div>
        <div className="col-span-12 md:col-span-4">
          <Card className="flex flex-col md:sticky md:top-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <CardHeader>
              <CardTitle>Cart</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 overflow-hidden">
              {/* Total and Checkout Button - Fixed at Top */}
              <div className="space-y-3 pb-4 border-b mb-4">
                <div className="text-sm flex justify-between"><span>Subtotal</span><span data-testid="subtotal">${subtotal.toFixed(2)}</span></div>
                <div className="text-sm flex justify-between"><span>Discounts</span><span data-testid="discount">-${discountAmount || discountPercent ? (discountAmount || (subtotal * discountPercent/100)).toFixed(2) : '0.00'}</span></div>
                <div className="text-sm flex justify-between"><span>Tax ({(taxRate * 100).toFixed(1)}%)</span><span data-testid="tax">${tax.toFixed(2)}</span></div>
                <div className="font-bold flex justify-between text-2xl pt-2 border-t"><span>Total</span><span data-testid="total">${total.toFixed(2)}</span></div>
                <Button data-testid="checkout-button" className="w-full h-14 text-xl font-semibold bg-black hover:bg-gray-800" onClick={checkout}>
                  Checkout
                </Button>
              </div>

              {/* Cart Items - Scrollable */}
              <div className="flex-1 overflow-y-auto space-y-3">
                {items.map((i) => (
                  <div key={i.sku} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-base">{i.name}</div>
                        {i.variantName && i.variantName !== 'Default' && (
                          <div className="text-sm text-primary mt-0.5">{i.variantName}</div>
                        )}
                        <div className="text-sm opacity-70 mt-1">${i.price.toFixed(2)} each</div>
                      </div>
                      <Button 
                        data-testid={`remove-${i.sku}`} 
                        variant="destructive" 
                        size="icon"
                        className="h-12 w-12 flex-shrink-0 text-xl" 
                        onClick={() => removeItem(i.sku)}
                      >
                        ×
                      </Button>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-12 w-12 text-xl"
                          onClick={() => updateQty(i.sku, Math.max(1, i.qty - 1))}
                        >
                          −
                        </Button>
                        <div className="w-16 text-center text-xl font-semibold">{i.qty}</div>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-12 w-12 text-xl"
                          onClick={() => updateQty(i.sku, i.qty + 1)}
                        >
                          +
                        </Button>
                      </div>
                      <div className="font-bold text-xl">${(i.qty * i.price).toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Variant Selection Modal */}
      <VariantSelectionModal
        isOpen={variantModalOpen}
        onClose={() => setVariantModalOpen(false)}
        product={selectedProduct}
        onSelectVariant={handleSelectVariant}
      />
      
      {/* Custom Item Modal */}
      <CustomItemModal
        open={customItemModalOpen}
        onClose={() => setCustomItemModalOpen(false)}
        onAdd={handleAddCustomItem}
      />
    </div>
  );
}
